/**
 * One row per captured activity: what happened, the detail, how long and
 * when. Agent rows are context only; they never count as billable time.
 */
import {
  Bot,
  Calendar,
  ChevronDown,
  FileText,
  Globe,
  Mail,
  type LucideIcon
} from "lucide-react";
import { useState, type FC } from "react";

import { cn } from "@/lib/utils";

import type { ActivityRow } from "../types";
import { formatDuration } from "../utils";

const ICONS: Record<string, LucideIcon> = {
  web_session: Globe,
  activity: FileText,
  email: Mail,
  calendar: Calendar,
  agent: Bot
};

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });

const rangeOf = (row: ActivityRow) => {
  if (!row.started_at) return "";
  const start = timeOf(row.started_at);
  if (!row.ended_at || row.ended_at === row.started_at) return start;
  return `${start} – ${timeOf(row.ended_at)}`;
};

type Props = {
  rows: readonly ActivityRow[];
  max?: number;
};

export const ActivityList: FC<Props> = ({ rows, max = 3 }) => {
  const [all, setAll] = useState(false);
  if (!rows.length) return null;
  const sorted = [...rows].sort((a, b) =>
    (a.started_at ?? "").localeCompare(b.started_at ?? "")
  );
  const shown = all ? sorted : sorted.slice(0, max);
  return (
    <div className="flex flex-col" data-testid="activity-list">
      {shown.map((row, i) => {
        const Icon = ICONS[row.kind] ?? FileText;
        const agent = row.kind === "agent" || !row.billable;
        return (
          <div
            key={row.id}
            className={cn(
              "grid grid-cols-[26px_minmax(0,1fr)_auto] items-center gap-2.5 py-1.5",
              i > 0 && "border-t border-line-2"
            )}
          >
            <span className="inline-flex size-6.5 items-center justify-center rounded-md bg-fill-2 text-fg-3">
              <Icon className="size-3" />
            </span>
            <span className="flex min-w-0 flex-col">
              <span
                className={cn(
                  "truncate text-sm font-medium",
                  agent && "text-fg-3"
                )}
              >
                {row.title}
              </span>
              <span className="truncate text-sm text-fg-3">
                {agent ? "Context only, not billed" : row.detail}
                {agent && row.detail ? ` · ${row.detail}` : ""}
              </span>
            </span>
            <span className="flex flex-col items-end">
              <span
                className={cn(
                  "text-sm font-medium tabular-nums",
                  agent ? "text-fg-3" : "text-fg"
                )}
              >
                {row.active_seconds ? formatDuration(row.active_seconds) : ""}
              </span>
              <span className="text-sm whitespace-nowrap text-fg-3 tabular-nums">
                {rangeOf(row)}
              </span>
            </span>
          </div>
        );
      })}
      {sorted.length > max ? (
        <button
          type="button"
          className="ml-8.5 inline-flex h-6 items-center gap-1 self-start rounded-md px-1.5 text-sm text-fg-3 hover:bg-fill-2"
          onClick={(e) => {
            e.stopPropagation();
            setAll((v) => !v);
          }}
        >
          {all ? "Show less" : `Show ${sorted.length - max} more`}
          <ChevronDown className={cn("size-3", all && "rotate-180")} />
        </button>
      ) : null}
    </div>
  );
};
